import { useLocalization } from "@faire/web--source/common/localization/useLocalization";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

import { blogPage, home } from "@faire/retailer/paths";
import { MakerSignUpMobileWrapper } from "@faire/retailer/views/apply/MakerApplication/MakerSignUp/MakerSignUpMobileWrapper";
import { MakerSignUpWrapper } from "@faire/retailer/views/apply/MakerApplication/MakerSignUp/MakerSignUpWrapper";

export enum SubmitPageStage {
  WAITLISTED = 1,
  UNSUPPORTED_AREA = 2,
}

interface IProps {
  onClickTellUsMore?: () => void;
  currentPage: SubmitPageStage;
}

export const MakerSignUpNotAccepted: React.FC<IProps> = ({
  currentPage,
  onClickTellUsMore,
}) => {
  return (
    <ResponsiveContentV2
      mobile={
        <MakerSignUpMobileWrapper>
          <PageWrapper>
            <Content currentPage={currentPage} hasCTA={!!onClickTellUsMore} />
            <Spacer height="3x" />
            <CTA {...{ onClickTellUsMore }} />
          </PageWrapper>
        </MakerSignUpMobileWrapper>
      }
      tabletAndAbove={
        <MakerSignUpWrapper>
          <PageWrapper>
            <Content currentPage={currentPage} hasCTA={!!onClickTellUsMore} />
            <CTA {...{ onClickTellUsMore }} />
          </PageWrapper>
        </MakerSignUpWrapper>
      }
    />
  );
};

const Content: React.FC<{ currentPage: SubmitPageStage; hasCTA: boolean }> = ({
  currentPage,
  hasCTA,
}) => {
  return (
    <Column data-test-id="waitlisted-message">
      <Typography variant="displayMSerifRegular">
        {currentPage === SubmitPageStage.WAITLISTED ? (
          <StrictLocalMsg defaultMessage="You've been added to our waitlist" />
        ) : (
          <StrictLocalMsg defaultMessage="Faire isn't available in your area yet" />
        )}
      </Typography>
      <Spacer height="3x" />
      <Typography>
        {currentPage === SubmitPageStage.WAITLISTED ? (
          <StrictLocalMsg defaultMessage="Our team will review your information and let you know if your shop is approved within 30 days. If you don't hear from us by then, it means we can't launch your shop on Faire right now - we’ll reach out if anything changes. In the meantime, you can add more details on the next page." />
        ) : (
          <StrictLocalMsg defaultMessage="Thank you for submitting your info! Unfortunately, we aren't in your area yet. Once we are, we'll reach out to you so you can set up your shop on Faire." />
        )}
      </Typography>
      {!hasCTA ? (
        <>
          <Spacer height="2x" />
          <Typography>
            <StrictLocalMsg defaultMessage="In the meantime, if you'd like to learn a little bit more about how brands use Faire for their wholesale business, you can visit our blog." />
          </Typography>
        </>
      ) : null}
    </Column>
  );
};

const CTA: React.FC<{ onClickTellUsMore?: () => void }> = ({
  onClickTellUsMore,
}) => {
  const {
    intl: { locale },
  } = useLocalization();
  if (onClickTellUsMore) {
    return (
      <CTAButton onClick={onClickTellUsMore}>
        <StrictLocalMsg
          defaultMessage="Add more details"
          description={{
            text: "CTA for prospective brand to tell us more information about themselves",
          }}
        />
      </CTAButton>
    );
  }

  return (
    <ButtonWrapper>
      <CTAButton linkTo={`${blogPage(locale)}/thestudio/`}>
        <StrictLocalMsg
          defaultMessage="Go to Faire Blog"
          description={{ text: "CTA link to visit Faire blog" }}
        />
      </CTAButton>
      <Spacer height="2x" />
      <Button variant="tertiary" linkTo={home()}>
        <StrictLocalMsg
          defaultMessage="Return to Faire.com"
          description={{ text: "Link that returns to faire.com homepage" }}
        />
      </Button>
    </ButtonWrapper>
  );
};

const ButtonWrapper = styled(Column).attrs({
  align: { mobileAndAbove: "center", tabletAndAbove: "flex-start" },
})`
  width: 100%;
`;

const PageWrapper = styled(Column).attrs({ justify: "space-between" })`
  ${responsiveMediaQueries.tabletAndAbove} {
    min-height: 500px;
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }
`;

const CTAButton = styled(Button).attrs({ variant: "primary" })`
  width: fit-content;
  min-width: 250px;
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;
